/**
 * Sell-channel presets.
 *
 * A sell channel describes where the reseller plans to flip an item, which
 * mainly changes the selling-cost percentage used in meat / walk-away math
 * (e.g. eBay shipped ≈ 14%, Facebook local ≈ 0%).
 *
 * Users may define custom presets (stored server-side in
 * profile.settings.sellChannels); otherwise the defaults below apply.
 * A lot references a channel by id via lot.sellChannel.
 *
 * @module portal/modules/sellChannels
 */

import type { PortalSettings } from '../types';

export interface SellChannelPreset {
  id: string;
  label: string;
  sellerCostsPct: number;
}

/** Built-in presets covering the common reseller exits. */
export const DEFAULT_SELL_CHANNELS: SellChannelPreset[] = [
  { id: 'ebay-shipped', label: 'eBay (shipped)', sellerCostsPct: 14 },
  { id: 'ebay-local', label: 'eBay (local pickup)', sellerCostsPct: 13 },
  { id: 'fb-local', label: 'Facebook Marketplace (local)', sellerCostsPct: 0 },
  { id: 'consignment', label: 'Consignment', sellerCostsPct: 30 },
];

function isValidPreset(value: unknown): value is SellChannelPreset {
  if (!value || typeof value !== 'object') return false;
  const preset = value as Partial<SellChannelPreset>;
  return (
    typeof preset.id === 'string' &&
    preset.id.length > 0 &&
    typeof preset.label === 'string' &&
    preset.label.length > 0 &&
    typeof preset.sellerCostsPct === 'number' &&
    Number.isFinite(preset.sellerCostsPct) &&
    preset.sellerCostsPct >= 0 &&
    preset.sellerCostsPct <= 100
  );
}

/** User presets from settings when defined, else the built-in defaults. */
export function getSellChannels(settings: PortalSettings | null | undefined): SellChannelPreset[] {
  const custom = settings?.sellChannels;
  if (Array.isArray(custom)) {
    const valid = custom.filter(isValidPreset);
    if (valid.length) return valid;
  }
  return DEFAULT_SELL_CHANNELS;
}

/** Look up a preset by id; null when not found or id is empty. */
export function getSellChannelById(
  settings: PortalSettings | null | undefined,
  id: string | null | undefined
): SellChannelPreset | null {
  if (!id) return null;
  return getSellChannels(settings).find(channel => channel.id === id) ?? null;
}
